// Intent: [Branding] [Conversion] [Journey]
import React from "react";
import { ChevronRightIcon, LogInIcon, MessageCircleIcon, UserPlusIcon } from "lucide-react";
import { IMAGE_ASSETS, PORTAL_LOGIN_URL } from "../../config";
type ActionTone = "navy" | "orange" | "secondary";
type ActionButtonProps = {
  href: string;
  title: string;
  subtitle: string;
  tone: ActionTone;
  icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  analytics?: string;
};
const toneStyles: Record<ActionTone, string> = {
  navy: "border-pfpc-navy bg-pfpc-navy text-pfpc-offwhite hover:bg-[#1f3350]",
  orange: "border-pfpc-orange bg-pfpc-orange text-pfpc-navy hover:bg-[#f07b41]",
  secondary: "border-pfpc-navy/25 bg-pfpc-offwhite text-pfpc-navy hover:border-pfpc-navy"
};
const iconToneStyles: Record<ActionTone, string> = {
  navy: "bg-pfpc-offwhite/15",
  orange: "bg-pfpc-navy/10",
  secondary: "bg-pfpc-navy/8"
};
function ActionButton({
  href,
  title,
  subtitle,
  tone,
  icon: Icon,
  analytics
}: ActionButtonProps) {
  return <a href={href} data-analytics={analytics} className={`group flex min-h-[108px] items-center gap-4 rounded-[10px] border-2 px-5 py-5 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pfpc-orange focus-visible:ring-offset-2 md:min-h-[118px] md:px-6 ${toneStyles[tone]}`} data-id="element-130" data-mp="9oueicg09o3u">
      <span className={`grid h-14 w-14 shrink-0 place-items-center rounded-[10px] ${iconToneStyles[tone]}`} data-id="element-131" data-mp="3rvs81lggcad3">
        <Icon className="h-7 w-7" strokeWidth={2} data-id="element-132" data-mp="nf7he0e1icmb" />
      </span>
      <span className="min-w-0 flex-1" data-id="element-133" data-mp="nfzfbhv40mfr">
        <span className="block font-heading text-[17px] font-extrabold uppercase leading-5 md:text-[18px]" data-id="element-134" data-mp="io92n58p9ml6">{title}</span>
        <span className="mt-1.5 block text-[14px] font-semibold leading-5 opacity-80" data-id="element-135" data-mp="enp3xx8haawx">{subtitle}</span>
      </span>
      <ChevronRightIcon className="h-6 w-6 shrink-0 transition-transform duration-200 group-hover:translate-x-1" strokeWidth={2.5} data-id="element-136" data-mp="1b5lew6fe93oa" />
    </a>;
}
function Hero() {
  return <section className="relative bg-pfpc-navy" data-id="element-137" data-mp="30lnn8la5g1qh">
      <div className="relative h-[420px] overflow-hidden sm:h-[480px] lg:h-[540px]" data-id="element-138" data-mp="30o0exq5plqgq">
        <img src={IMAGE_ASSETS.heroKitchen} alt="Illustrative custom flat pack cabinetry with a cabinetry plan open on a laptop" className="absolute inset-0 h-full w-full object-cover" loading="eager" data-id="element-139" data-mp="35kh48nonaave" />
        <div className="absolute inset-0 bg-pfpc-navy/45" aria-hidden="true" data-id="element-140" data-mp="2hvpj2g9h2ss4" />
        <div className="container-shell relative flex h-full items-center" data-id="element-141" data-mp="2hxsczbdqx624">
          <div className="max-w-xl" data-id="element-142" data-mp="2mvtzy7ealpiv">
            <p className="section-label text-pfpc-orange" data-id="element-143" data-mp="27xi2hyj3icqn">Cabinetry Online · Perth</p>
            <h1 className="mt-4 font-heading text-[34px] font-extrabold leading-[1.1] text-pfpc-offwhite sm:text-[44px] lg:text-[52px]" data-id="element-144" data-mp="2cwcv1s6v7fny">
              Design your own custom cabinets online.
            </h1>
            <p className="mt-5 max-w-[50ch] text-[17px] leading-7 text-pfpc-offwhite/85 lg:text-lg" data-id="element-145" data-mp="b4q0jgnrl2mq">
              Choose Trades or Do It Yourself - Easy, create your cabinetry job with the tools available in Cabinetry.Online, then submit it for review.
            </p>
          </div>
        </div>
        <span className="absolute bottom-3 right-4 text-[11px] font-semibold text-pfpc-offwhite/60" data-id="element-146" data-mp="25ck26dt28kir">Illustrative concept image</span>
      </div>

      <div id="account-actions" className="scroll-mt-24 border-t border-pfpc-navy/10 bg-pfpc-offwhite py-5 md:py-6" data-id="element-147" data-mp="20lbtxl3gv576">
        <div className="container-shell" data-id="element-148" data-mp="2ebp1vngcyjwi">
          <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between" data-id="element-149" data-mp="2acg6ke5o6dyu">
            <h2 className="font-heading text-xl font-extrabold text-pfpc-navy md:text-2xl" data-id="element-150" data-mp="2obfypvse5azr">Choose how you want to start.</h2>
            <p className="text-[14px] text-pfpc-navy/60" data-id="element-151" data-mp="2jdmsj2r38gzh">Three clear paths. No quote form required first.</p>
          </div>
          <div className="grid gap-3 lg:grid-cols-3" data-id="element-152" data-mp="txx2qqe0e0gk">
            <ActionButton href="/register/trade/" title="Trades" subtitle="For installers and trade customers with project details ready." tone="navy" icon={UserPlusIcon} analytics="trade_register_clicked" data-id="element-153" data-mp="oz3lcmvlppb4" />
            <ActionButton href="/register/diy/" title="Do It Yourself - Easy" subtitle="For capable renovators ready to prepare and configure a project." tone="orange" icon={UserPlusIcon} analytics="diy_register_clicked" data-id="element-154" data-mp="ve137qxjgscr" />
            <ActionButton href="/help/" title="Need Help First?" subtitle="Ask a Cabinetry.Online question before registering." tone="secondary" icon={MessageCircleIcon} analytics="contact_clicked" data-id="element-155" data-mp="15box6enblvtv" />
          </div>
          <div className="mt-3 flex flex-col items-center justify-center gap-x-7 sm:flex-row sm:flex-wrap" data-id="element-156" data-mp="com2xypog79f">
            <a href={PORTAL_LOGIN_URL} data-analytics="portal_login_clicked" className="inline-flex min-h-11 items-center gap-2 py-2 text-sm font-bold text-pfpc-navy underline decoration-pfpc-navy/25 underline-offset-4 hover:decoration-pfpc-orange focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pfpc-orange" data-id="element-157" data-mp="hlckyrl1lcxh">
              <LogInIcon className="h-4 w-4" data-id="element-158" data-mp="277up8ojhp4t2" /> Already registered? Log in
            </a>
            <a href="/help/" className="inline-flex min-h-11 items-center py-2 text-center text-[13px] font-semibold text-pfpc-navy/70 underline decoration-pfpc-orange underline-offset-4 hover:text-pfpc-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pfpc-orange" data-id="element-159" data-mp="236j7142y3luw">
              Builder, Multi-Residential or Mining project? Contact the team.
            </a>
          </div>
        </div>
      </div>
    </section>;
}
export { Hero };